import { SIDEBAR_MODULE_CONFIG } from '@/layouts/admin/sidebar-config'

/**
 * Resolve, a partir do pathname, qual permissao a rota exige — reaproveitando o
 * SIDEBAR_MODULE_CONFIG (que ja mapeia cada recurso ao seu href). Usado pelo
 * guard de rota do admin para bloquear acesso direto via URL a modulos/recursos
 * que o usuario nao possui (defesa em profundidade; o backend ja bloqueia dados).
 */

interface AccessEntry {
    prefix: string
    module: string
    resource: string
}

// (pathPrefix -> module:resource) para cada recurso do sidebar, ordenado do
// prefixo mais longo (mais especifico) para o mais curto.
const ACCESS_ENTRIES: AccessEntry[] = (() => {
    const entries: AccessEntry[] = []
    for (const [moduleKey, cfg] of Object.entries(SIDEBAR_MODULE_CONFIG)) {
        for (const [resourceKey, res] of Object.entries(cfg.resources)) {
            const prefix = res.href.split('?')[0]
            entries.push({ prefix, module: moduleKey, resource: resourceKey })
        }
    }
    entries.sort((a, b) => b.prefix.length - a.prefix.length)
    return entries
})()

const MODULE_KEYS = new Set(Object.keys(SIDEBAR_MODULE_CONFIG))

function matchesPrefix(pathname: string, prefix: string): boolean {
    return pathname === prefix || pathname.startsWith(prefix + '/')
}

export type RouteRequirement =
    | { kind: 'resource'; options: Array<{ module: string; resource: string }> }
    | { kind: 'module'; module: string }
    | null

/**
 * - `resource`: a rota casa um (ou mais) recursos do menu — exige acesso a pelo
 *   menos um deles.
 * - `module`: nao casou recurso, mas o 1o segmento eh um modulo conhecido —
 *   exige acesso ao modulo (fecha sub-rotas nao mapeadas).
 * - `null`: rota neutra (welcome, /account, /core/*, etc) — sempre liberada.
 */
export function resolveRouteRequirement(pathname: string): RouteRequirement {
    const path = pathname.split('?')[0]

    const matched = ACCESS_ENTRIES.filter(e => matchesPrefix(path, e.prefix))
    if (matched.length > 0) {
        const maxLen = matched[0].prefix.length // ACCESS_ENTRIES ja ordenado desc
        const best = matched.filter(e => e.prefix.length === maxLen)
        return {
            kind: 'resource',
            options: best.map(e => ({ module: e.module, resource: e.resource })),
        }
    }

    const seg = /^\/([a-z0-9-]+)/i
        .exec(path)?.[1]
        ?.toLowerCase()
        .replace(/-/g, '_')
    if (seg && MODULE_KEYS.has(seg)) {
        return { kind: 'module', module: seg }
    }

    return null
}
